import { Grid } from '@mui/material';
import {FC, useState} from 'react';
import { Control, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {Select} from 'thermia-react-container';
import {CustomerTypeConfigWithDisplayDescription, useRegistrationConfigHook} from "../../../hooks/useRegistrationConfigHook";

type InsuranceSelectorModalProps = {
  control: Control<any>;
  onChangeCustomerType: (selectedCustomerCode: string) => void;
  disabled?: boolean;
};

const InsuranceSelector: FC<InsuranceSelectorModalProps> = ({ control, disabled, onChangeCustomerType }) => {
  const customerTypeController = useController({
    name: 'type',
    control: control
  });

  const { t } = useTranslation('registrations');
  const { validCustomerTypes, getAvailableInsurancesWithDescription } = useRegistrationConfigHook();

  const [availableInsurances, setAvailableInsurances] = useState<CustomerTypeConfigWithDisplayDescription[]>(
    getAvailableInsurancesWithDescription(customerTypeController.field.value)
  );

  const insuranceTypeController = useController({
    name: 'insuranceType',
    control: control
  });
    
  const onCustomerTypeClick = (selectedCustomerCode: string) => {
    customerTypeController.field.onChange(selectedCustomerCode);

    const validInsuranceTypes = getAvailableInsurancesWithDescription(selectedCustomerCode);
    if (!validInsuranceTypes.includes(insuranceTypeController.field.value)) {
      if (validInsuranceTypes.length === 1) {
        insuranceTypeController.field.onChange(validInsuranceTypes[0].code);
      } else {
        insuranceTypeController.field.onChange('');
      }
    }
    setAvailableInsurances(validInsuranceTypes);
    onChangeCustomerType(selectedCustomerCode);
  };

  const onInsuranceTypeClick = (selectedInsuranceType: string) => {
    insuranceTypeController.field.onChange(selectedInsuranceType);
  };

  return (
    <>
      <Grid item xs={12}>
        <Select
          label={t('registrations.general.selectCustomerType')}
          labelId="customerType-select-label"
          variant="outlined"
          value={customerTypeController.field.value}
          onChange={onCustomerTypeClick}
          disabled={disabled || validCustomerTypes.length === 1}
          error={customerTypeController.fieldState.error?.message}
          options={validCustomerTypes.map((customerType: { code: string; displayName: string }) => ({
            value: customerType.code,
            label: t('registrations.insuranceDescriptions.' + customerType.displayName)
          }))}
        />
      </Grid>

      <Grid item xs={12}>
        <Select
          labelId="insurance-select-label"
          label={t('registrations.general.selectInsurance')}
          variant="outlined"
          value={insuranceTypeController.field.value}
          onChange={onInsuranceTypeClick}
          disabled={disabled || availableInsurances.length === 1}
          error={insuranceTypeController.fieldState.error?.message && t(insuranceTypeController.fieldState.error.message)}
          options={availableInsurances.map((insurance) => ({
            label: insurance.displayDescription,
            value: insurance.code
          }))}
        />
      </Grid>
    </>
  );
};

export default InsuranceSelector;
